import React, {useContext, useEffect, useRef, useState} from 'react'
import {useNavigate, useParams, useBlocker} from 'react-router-dom'
import AppContext from '../../appContext'
import LimeController from '../../controllers/LimeController'
import LimeProgramResponse from '../../controllers/LimeProgramResponse'
import LimeProgramLandResponse from '../../controllers/LimeProgramLandResponse'
import LimeProgramLabInputs from '../../controllers/LimeProgramLabInputs'
import LimeProgramProductSources from '../../controllers/LimeProgramProductSources'
import LimePlayFieldRow from '../../controllers/LimePlayFieldRow'
import LimeTemplateBasicResult from '../../controllers/LimeTemplateBasicResult'
import LimeProductResponse from '../../controllers/LimeProductResponse'
import LimeProductType from '../../controllers/LimeProductType'
import {dateFormat} from '../../date'
import Dialog from '../../components/Dialog'
import ProgramApprove from '../program/ProgramApprove'
import {showSuccessOrFailed} from '../../Snacks'
import {ChevronLeftIcon, ChevronRightIcon} from '@heroicons/react/24/outline'
import Failed from '../../components/Failed'
import {calculateLandRows} from './limeCalcs'
import {POTASSIUM_SOURCES, PHOSPHATE_SOURCES} from './staticdata'
import LimeSoilSummary from './LimeSoilSummary'
import SelectNumber from '../../components/SelectNumber'
import SelectNumberNullable from '../../components/SelectNumberNullable'
import SelectString from '../../components/SelectString'
import Input from '../../components/Input'
import Number from '../../components/Number'

interface LimeProgramProps {
    id?: number
    footer?: React.ReactNode
}

type ActiveTab = 'master' | number

interface LandTabData {
    land: LimeProgramLandResponse
    labInputs: LimeProgramLabInputs
    calculatedRows: LimePlayFieldRow[]
    changed: boolean
}

const LimeProgram: React.FC<LimeProgramProps> = (props) => {
    const context = useContext(AppContext)
    const navigate = useNavigate()
    const params = useParams()
    const id = props.id ?? parseInt(params.id as string)

    const [program, setProgram] = useState<LimeProgramResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [showApprover, setShowApprover] = useState(false)
    const [warningPopup, setWarningPopup] = useState(false)
    const [tab, setTab] = useState<ActiveTab>('master')

    // Editable state
    const [name, setName] = useState('')
    const [productSources, setProductSources] = useState<LimeProgramProductSources | null>(null)
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)
    const [templates, setTemplates] = useState<LimeTemplateBasicResult[]>([])
    const [products, setProducts] = useState<LimeProductResponse[]>([])
    const [landTabs, setLandTabs] = useState<LandTabData[]>([])
    const [deletedLandIds, setDeletedLandIds] = useState<number[]>([])

    // Track original values
    const originalRef = useRef<{
        name: string
        productSources: LimeProgramProductSources
        templateId: number
        lands: LimeProgramLandResponse[]
    } | null>(null)

    useEffect(() => {
        if (isNaN(id)) {
            navigate('/liming/programs')
            return
        }

        Promise.all([
            LimeController.getProgram(id),
            LimeController.getApprovedTemplates(),
            LimeController.getAllProducts()
        ]).then(([data, templatesData, productsData]) => {
            setProgram(data)
            setName(data.name)
            setProductSources(data.productSources)
            setSelectedTemplateId(data.limeTemplateId)
            setTemplates(templatesData)
            setProducts(productsData)

            // Build land tabs with calculations
            const tabs: LandTabData[] = data.lands.map(land => ({
                land,
                labInputs: land.labInputs,
                calculatedRows: data.templateData ? calculateLandRows(land.labInputs, data.templateData) : [],
                changed: false
            }))
            setLandTabs(tabs)

            // Store original for change detection
            originalRef.current = {
                name: data.name,
                productSources: data.productSources,
                templateId: data.limeTemplateId,
                lands: data.lands
            }

            setLoading(false)
        }).catch(() => {
            context.showSnack(<Failed title={context.word('failed_to_load')}/>)
            navigate('/liming/programs')
        })
    }, [id])

    // Detect unsaved changes
    const hasChanges = (): boolean => {
        if (!originalRef.current || !program) return false

        if (name !== originalRef.current.name) return true
        if (selectedTemplateId !== originalRef.current.templateId) return true
        if (JSON.stringify(productSources) !== JSON.stringify(originalRef.current.productSources)) return true
        if (deletedLandIds.length > 0) return true
        if (landTabs.some(t => t.changed)) return true

        return false
    }

    // Block navigation when there are unsaved changes
    const blocker = useBlocker(() => hasChanges())

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (hasChanges()) {
                event.preventDefault()
            }
        }
        window.addEventListener('beforeunload', handleBeforeUnload)
        return () => window.removeEventListener('beforeunload', handleBeforeUnload)
    }, [name, productSources, selectedTemplateId, landTabs, deletedLandIds])

    function nextTab(): ActiveTab {
        if (tab === 'master') {
            return landTabs.length > 0 ? 0 : 'master'
        }
        const current = tab as number
        return current < landTabs.length - 1 ? current + 1 : 'master'
    }

    function prevTab(): ActiveTab {
        if (tab === 'master') {
            return landTabs.length > 0 ? landTabs.length - 1 : 'master'
        }
        const current = tab as number
        return current > 0 ? current - 1 : 'master'
    }

    function saveProgram(): Promise<void> {
        if (!program || !productSources || !selectedTemplateId) {
            return Promise.reject()
        }

        const lands = landTabs.map((t, index) => ({
            landId: t.land.landId,
            order: index,
            labInputs: t.labInputs
        }))

        return showSuccessOrFailed(
            context,
            LimeController.updateProgram({
                id: program.id,
                name,
                limeTemplateId: selectedTemplateId,
                productSources,
                lands,
                deleteLandIds: deletedLandIds
            }),
            'successfully_saved'
        ).then(() => {
            // Update original ref after save
            originalRef.current = {
                name,
                productSources,
                templateId: selectedTemplateId,
                lands: landTabs.map(t => t.land)
            }
            // Mark all as unchanged
            setLandTabs(landTabs.map(t => ({...t, changed: false})))
            setDeletedLandIds([])

            // Reset approval status after saving (must resubmit)
            if (program) {
                setProgram({
                    ...program,
                    submittedOn: null,
                    approvedOn: null,
                    approvedBy: null
                })
            }
        })
    }

    function submitProgram(approverId: number) {
        showSuccessOrFailed(context, LimeController.submitProgram({id, approverId}))
            .then(() => {
                setShowApprover(false)
                if (program) {
                    setProgram({
                        ...program,
                        submittedOn: new Date(),
                        assignedApproverId: approverId
                    })
                }
            })
    }

    function updateLandLabInputs(index: number, labInputs: LimeProgramLabInputs) {
        setLandTabs(prev => prev.map((t, i) => {
            if (i === index) {
                const calculatedRows = program?.templateData
                    ? calculateLandRows(labInputs, program.templateData)
                    : []
                return {...t, labInputs, calculatedRows, changed: true}
            }
            return t
        }))
    }

    function renderTab(key: ActiveTab, tabName: string, modified: boolean) {
        const isActive = key === tab
        const style = isActive
            ? 'flex bg-white px-2 py-1 border-l border-r border-r-4 border-gray-300 rounded-t border-l-gray-300 border-t-2 border-t-primary-600 cursor-pointer'
            : 'flex px-2 py-1 border-r-4 border-gray-100 cursor-pointer'

        return (
            <div
                key={key}
                className={style}
                onClick={() => setTab(key)}
            >
                <span className="text-red-500">{modified ? '*' : ' '}</span>
                <span className="px-2 align-middle text-black whitespace-nowrap">{tabName}</span>
            </div>
        )
    }

    function renderMasterTab() {
        if (!program || !productSources) return null

        const isSubmitted = program.submittedOn !== null
        const isApproved = program.approvedOn !== null

        return (
            <div className="p-4">
                <div className="flex w-full justify-center">
                    <div className="pt-4">
                        <div className="text-sm text-gray-600">{dateFormat(new Date())}</div>
                        <div className="font-bold uppercase text-primary-800 tracking-wider text-xl leading-none mb-4">
                            {program.clientName}
                        </div>

                        <table>
                            <tbody>
                                <tr>
                                    <td className="p-1">{context.word('program_name')}:</td>
                                    <td className="p-1">
                                        <Input
                                            value={name}
                                            change={setName}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-1">{context.word('farm')}:</td>
                                    <td className="p-1 font-medium">{program.farmName}</td>
                                </tr>
                                <tr>
                                    <td className="p-1">{context.word('template')}:</td>
                                    <td className="p-1">
                                        <SelectNumber
                                            options={templates}
                                            textFunc={t => t.name}
                                            valueFunc={t => t.id}
                                            value={selectedTemplateId || 0}
                                            onChange={v => setSelectedTemplateId(v)}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-1">{context.word('calcitic_lime_product')}:</td>
                                    <td className="p-1">
                                        <SelectNumberNullable
                                            options={products.filter(p => p.type === LimeProductType.Calcite && p.active)}
                                            textFunc={p => p.name}
                                            valueFunc={p => p.id}
                                            value={productSources.calciticLimeProductId}
                                            onChange={v => setProductSources({...productSources, calciticLimeProductId: v})}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-1">{context.word('dolomitic_lime_product')}:</td>
                                    <td className="p-1">
                                        <SelectNumberNullable
                                            options={products.filter(p => p.type === LimeProductType.Dolomite && p.active)}
                                            textFunc={p => p.name}
                                            valueFunc={p => p.id}
                                            value={productSources.dolomiticLimeProductId}
                                            onChange={v => setProductSources({...productSources, dolomiticLimeProductId: v})}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-1">{context.word('gypsum_product')}:</td>
                                    <td className="p-1">
                                        <SelectNumberNullable
                                            options={products.filter(p => p.type === LimeProductType.Gypsum && p.active)}
                                            textFunc={p => p.name}
                                            valueFunc={p => p.id}
                                            value={productSources.gypsumProductId}
                                            onChange={v => setProductSources({...productSources, gypsumProductId: v})}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-1">{context.word('potassium_source')}:</td>
                                    <td className="p-1">
                                        <SelectString
                                            options={[...POTASSIUM_SOURCES]}
                                            textFunc={s => s}
                                            valueFunc={s => s}
                                            value={productSources.potassiumSource}
                                            onChange={v => setProductSources({...productSources, potassiumSource: v})}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-1">{context.word('phosphate_source')}:</td>
                                    <td className="p-1">
                                        <SelectString
                                            options={[...PHOSPHATE_SOURCES]}
                                            textFunc={s => s}
                                            valueFunc={s => s}
                                            value={productSources.phosphateSource}
                                            onChange={v => setProductSources({...productSources, phosphateSource: v})}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-1">{context.word('lime_gypsum_depth')} (cm):</td>
                                    <td className="p-1">
                                        <Number
                                            value={productSources.limeGypsumDepthCm}
                                            change={v => setProductSources({...productSources, limeGypsumDepthCm: v})}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td className="p-1">{context.word('pk_depth')} (cm):</td>
                                    <td className="p-1">
                                        <Number
                                            value={productSources.pkDepthCm}
                                            change={v => setProductSources({...productSources, pkDepthCm: v})}
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <div className="mt-4 text-sm">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <span className="text-gray-600">{context.word('created')}:</span>
                                    <span className="ml-2">{dateFormat(program.created)}</span>
                                </div>
                                <div>
                                    <span className="text-gray-600">{context.word('updated')}:</span>
                                    <span className="ml-2">{dateFormat(program.updated)}</span>
                                </div>
                                {isSubmitted && (
                                    <div>
                                        <span className="text-gray-600">{context.word('submitted')}:</span>
                                        <span className="ml-2">{dateFormat(program.submittedOn)}</span>
                                    </div>
                                )}
                                {isApproved && (
                                    <>
                                        <div>
                                            <span className="text-gray-600">{context.word('approved')}:</span>
                                            <span className="ml-2">{dateFormat(program.approvedOn)}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">{context.word('approved_by')}:</span>
                                            <span className="ml-2">{program.approvedBy}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function renderLandTab(index: number) {
        const landTab = landTabs[index]
        if (!landTab) return null

        const isApproved = program?.approvedOn !== null

        return (
            <div className="mx-3 mb-1 border-2 border-gray-100">
                
                {/* Soil Data at top - like fertilizer's SoilSummary */}
                <div className="mb-6 border-2 border-gray-100 p-2">
                    <LimeSoilSummary
                        data={landTab.labInputs}
                        setData={(labInputs) => updateLandLabInputs(index, labInputs)}
                    />
                </div>

                {landTab.calculatedRows.length === 0 ? (
                    <div className="text-gray-500">{context.word('no_recommendations')}</div>
                ) : (
                    <div className="overflow-x-auto">
                        {/* Recommendations Table */}
                        <h3 className="font-semibold mb-2">{context.word('recommendations')}</h3>
                        <table className="min-w-full text-xs mb-6">
                            <thead className="bg-primary-50">
                                <tr>
                                    <th className="px-2 py-2 text-left font-medium">Ref</th>
                                    <th className="px-2 py-2 text-left font-medium">Lime Type</th>
                                    <th className="px-2 py-2 text-right font-medium">Lime Y1 (kg/ha)</th>
                                    <th className="px-2 py-2 text-right font-medium">Lime Y2 (kg/ha)</th>
                                    <th className="px-2 py-2 text-right font-medium">Lime Y3 (kg/ha)</th>
                                    <th className="px-2 py-2 text-right font-medium">Gypsum Y1 (kg/ha)</th>
                                    <th className="px-2 py-2 text-right font-medium">Gypsum Y2 (kg/ha)</th>
                                    <th className="px-2 py-2 text-right font-medium">K Fert Y1 (kg/ha)</th>
                                    <th className="px-2 py-2 text-right font-medium">K Fert Y2 (kg/ha)</th>
                                    <th className="px-2 py-2 text-right font-medium">P Fert Y1 (kg/ha)</th>
                                    <th className="px-2 py-2 text-right font-medium">P Fert Y2 (kg/ha)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {landTab.calculatedRows.map((row, idx) => (
                                    <tr key={idx} className="border-t hover:bg-gray-50">
                                        <td className="px-2 py-2">{row.referenceNr}</td>
                                        <td className="px-2 py-2">
                                            <span className={`px-2 py-0.5 rounded text-xs ${
                                                row.limeType === 'Dolomitic'
                                                    ? 'bg-purple-100 text-purple-800'
                                                    : 'bg-blue-100 text-blue-800'
                                            }`}>
                                                {row.limeType}
                                            </span>
                                        </td>
                                        <td className="px-2 py-2 text-right font-mono">
                                            {row.appliedYear1KgHa > 0 ? row.appliedYear1KgHa.toFixed(0) : '-'}
                                        </td>
                                        <td className="px-2 py-2 text-right font-mono">
                                            {row.appliedYear2KgHa > 0 ? row.appliedYear2KgHa.toFixed(0) : '-'}
                                        </td>
                                        <td className="px-2 py-2 text-right font-mono">
                                            {row.appliedYear3KgHa > 0 ? row.appliedYear3KgHa.toFixed(0) : '-'}
                                        </td>
                                        <td className="px-2 py-2 text-right font-mono">
                                            {row.gypsumYear1KgHa > 0 ? row.gypsumYear1KgHa.toFixed(0) : '-'}
                                        </td>
                                        <td className="px-2 py-2 text-right font-mono">
                                            {row.gypsumYear2KgHa > 0 ? row.gypsumYear2KgHa.toFixed(0) : '-'}
                                        </td>
                                        <td className="px-2 py-2 text-right font-mono">
                                            {row.kFertYear1KgHa > 0 ? row.kFertYear1KgHa.toFixed(0) : '-'}
                                        </td>
                                        <td className="px-2 py-2 text-right font-mono">
                                            {row.kFertYear2KgHa > 0 ? row.kFertYear2KgHa.toFixed(0) : '-'}
                                        </td>
                                        <td className="px-2 py-2 text-right font-mono">
                                            {row.pFertYear1KgHa > 0 ? row.pFertYear1KgHa.toFixed(0) : '-'}
                                        </td>
                                        <td className="px-2 py-2 text-right font-mono">
                                            {row.pFertYear2KgHa > 0 ? row.pFertYear2KgHa.toFixed(0) : '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Summary stats */}
                        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 rounded p-3">
                            <div>
                                <div className="text-xs text-gray-500">{context.word('total_lime_y1')}</div>
                                <div className="font-semibold">
                                    {landTab.calculatedRows.reduce((sum, r) => sum + r.appliedYear1KgHa, 0).toFixed(0)} kg/ha
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500">{context.word('total_gypsum_y1')}</div>
                                <div className="font-semibold">
                                    {landTab.calculatedRows.reduce((sum, r) => sum + r.gypsumYear1KgHa, 0).toFixed(0)} kg/ha
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500">{context.word('total_k_fert_y1')}</div>
                                <div className="font-semibold">
                                    {landTab.calculatedRows.reduce((sum, r) => sum + r.kFertYear1KgHa, 0).toFixed(0)} kg/ha
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500">{context.word('total_p_fert_y1')}</div>
                                <div className="font-semibold">
                                    {landTab.calculatedRows.reduce((sum, r) => sum + r.pFertYear1KgHa, 0).toFixed(0)} kg/ha
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    if (loading) {
        return <div className="p-4">{context.word('loading')}...</div>
    }

    if (!program) {
        return <div className="p-4">{context.word('program_not_found')}</div>
    }

    const isSubmitted = program.submittedOn !== null
    const isApproved = program.approvedOn !== null
    const masterChanged = name !== originalRef.current?.name ||
        selectedTemplateId !== originalRef.current?.templateId ||
        JSON.stringify(productSources) !== JSON.stringify(originalRef.current?.productSources)
    const somethingChanged = hasChanges()

    return (
        <div>
            {/* Dialogs */}
            <Dialog
                title={context.word('select_approver')}
                body={<ProgramApprove onCancel={() => setShowApprover(false)} onSubmit={submitProgram}/>}
                show={showApprover}
                setShow={setShowApprover}
            />

            <Dialog
                title={context.word('warning')}
                show={warningPopup}
                setShow={setWarningPopup}
                body={
                    <div className="p-2 text-xl">
                        <div>{context.word('must_resubmit_program_for_approval')}. {context.word('would_you_like_to_continue')}?</div>
                        <div className="flex m-2 text-white justify-center">
                            <button
                                className="bg-primary p-2 rounded m-1 px-4"
                                onClick={() => {
                                    saveProgram()
                                    setWarningPopup(false)
                                }}
                            >
                                {context.word('yes')}
                            </button>
                            <button
                                className="bg-error-500 p-2 rounded m-1 px-4"
                                onClick={() => setWarningPopup(false)}
                            >
                                {context.word('no')}
                            </button>
                        </div>
                    </div>
                }
            />

            <Dialog
                title={context.word('save_changes')}
                show={blocker.state === 'blocked'}
                setShow={() => blocker.reset?.()}
                body={
                    <div className="p-4">
                        <div className="text-center mb-4">
                            <div>{context.word('unsaved_changes_lost')}.</div>
                            <div>{context.word('save_before_closing')}?</div>
                        </div>
                        <div className="flex w-full justify-between items-center">
                            <div
                                className="uppercase text-error-400 pr-4 cursor-pointer underline"
                                onClick={() => blocker.proceed?.()}
                            >
                                {context.word('discard_changes')}
                            </div>
                            <div
                                className="btn bg-primary"
                                onClick={() => {
                                    saveProgram().then(() => {
                                        blocker.proceed?.()
                                    })
                                }}
                            >
                                {context.word('save')}
                            </div>
                        </div>
                    </div>
                }
            />

            {/* Tabs */}
            <div className="sticky top-0 z-10">
                <div className="bg-gray-100 flex overflow-x-scroll pt-1 text-sm items-center overflow-hidden">
                    <div className="px-4"></div>
                    <div className="cursor-pointer">
                        <ChevronLeftIcon className="text-gray-500 h-4" onClick={() => setTab(prevTab())}/>
                    </div>
                    <div className="cursor-pointer">
                        <ChevronRightIcon className="text-gray-500 h-4" onClick={() => setTab(nextTab())}/>
                    </div>
                    <div className="w-4"></div>

                    {renderTab('master', context.word('master'), masterChanged)}
                    {landTabs.map((landTab, index) => renderTab(
                        index,
                        landTab.land.landName,
                        landTab.changed
                    ))}
                </div>
            </div>

            {/* Tab Content */}
            {tab === 'master' ? renderMasterTab() : renderLandTab(tab as number)}

            {/* Footer Actions */}
            <div className="text-right pr-8">
                {somethingChanged && (
                    <div
                        className="btn bg-primary m-2"
                        onClick={() => {
                            if (isSubmitted || isApproved) {
                                setWarningPopup(true)
                            } else {
                                saveProgram()
                            }
                        }}
                    >
                        {context.word('save_all')}
                    </div>
                )}

                {!props.footer && !isSubmitted && !isApproved && (
                    <div
                        className="btn bg-primary m-2"
                        onClick={() => setShowApprover(true)}
                    >
                        {context.word('submit_approval')}
                    </div>
                )}

                {!props.footer && isSubmitted && !isApproved && (
                    <div className="btn bg-gray-500 m-2">
                        {context.word('waiting_approval')}
                    </div>
                )}

                {props.footer}
            </div>
        </div>
    )
}

export default LimeProgram
